/**
 * DispoActionModals — Jun-2026 (User-Wunsch).
 *
 * - DeliveryOrderModal: legt zu einem Fahrzeug einen internen Auslieferungs-/
 *   Vorbereitungsauftrag (Werkstatt) mit den auszuführenden Arbeiten an.
 * - TransportOrderModal: erstellt einen Transportauftrag (Transportplanung)
 *   für das Fahrzeug (Von/Nach, Termin, Transporteur).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Truck, Eye } from "lucide-react";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const vehLabel = (it) =>
    [it?.brand, it?.model, it?.license_plate].filter(Boolean).join(" ") || it?.title || "Fahrzeug";

export function DeliveryOrderModal({ item, onClose }) {
    const navigate = useNavigate();
    const [work, setWork] = useState("");
    // Aug-2026 (User-Wunsch): EIN gemeinsamer Termin — Auslieferungstermin aus dem
    // Kaufvertrag vorbefüllen (Backend fällt sonst selbst auf die Dispo zurück).
    const [appt, setAppt] = useState((item?.dispo_delivery?.delivery_date || "").slice(0, 10));
    const [busy, setBusy] = useState(false);
    const [previewing, setPreviewing] = useState(false);

    const preview = async () => {
        if (!work.trim()) { toast.error("Bitte zuerst die auszuführenden Arbeiten angeben."); return; }
        setPreviewing(true);
        try {
            const res = await api.post("/dispo/delivery-order/preview-pdf",
                { listing_id: item.id, work_text: work.trim(), appointment_at: appt ? `${appt}T09:00:00` : null },
                { responseType: "blob" });
            previewPdfResponse(res, "Auslieferungsauftrag_Vorschau.pdf");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen.");
        } finally { setPreviewing(false); }
    };

    const submit = async () => {
        if (!work.trim()) { toast.error("Bitte die auszuführenden Arbeiten angeben."); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/dispo/delivery-order", {
                listing_id: item.id, work_text: work.trim(),
                appointment_at: appt ? `${appt}T09:00:00` : null,
            });
            // Jun-2026 (User-Wunsch): direkt zum Auftrag springen, kein Bestätigungsfenster.
            toast.success(`Auslieferungsauftrag ${data.order_number} angelegt.`);
            onClose?.();
            navigate(`/dealer/workshop-orders?focus=${data.order_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen.");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="delivery-order-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5 text-[#111827]" /> Interner Auslieferungsauftrag
                    </DialogTitle>
                    <DialogDescription>{vehLabel(item)}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Auszuführende Arbeiten *</label>
                        <Textarea value={work} onChange={(e) => setWork(e.target.value)} rows={5}
                            placeholder="z. B. Wäsche, Auslieferungsinspektion, Zubehör montieren, Probelauf, Kennzeichen anbringen…"
                            className="rounded-sm mt-1" data-testid="delivery-order-work" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Auslieferungstermin (= Wunschtermin Fertigstellung)</label>
                        <Input type="date" value={appt} onChange={(e) => setAppt(e.target.value)}
                            className="rounded-sm mt-1" data-testid="delivery-order-appt" />
                        {item?.dispo_delivery?.delivery_date && (
                            <p className="text-[10px] text-gray-400 mt-0.5">Aus dem Kaufvertrag übernommen — Änderungen am Auslieferungstermin werden automatisch in den Auftrag gespiegelt.</p>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-400">Wird als Werkstattauftrag (Typ „Auslieferung intern") angelegt und erscheint in der Werkstatt-Auftragsliste. Mit Wunschtermin erscheint er zusätzlich im Terminkalender. Tipp: Vorab als PDF prüfen.</p>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                    <Button variant="outline" onClick={preview} disabled={previewing || busy} className="rounded-sm gap-1.5" data-testid="delivery-order-preview">
                        <Eye className="w-4 h-4" /> {previewing ? "Erstelle…" : "Vorschau"}
                    </Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="delivery-order-submit">
                        {busy ? "Lege an…" : "Auftrag anlegen"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function TransportOrderModal({ item, onClose }) {
    const navigate = useNavigate();
    const [f, setF] = useState({
        from_location: "", to_location: "", scheduled_date: "",
        time_window: "", carrier: "", driver: "", note: "",
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const submit = async (goToPlanning) => {
        setBusy(true);
        try {
            await api.post("/transport-orders", {
                listing_id: item.id,
                vehicle: { brand: item.brand, model: item.model, vin: item.vin,
                           license_plate: item.license_plate, title: item.title, listing_id: item.id },
                ...f,
            });
            toast.success("Transportauftrag erstellt.");
            onClose?.();
            if (goToPlanning) navigate("/dealer/transport");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen.");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="transport-order-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-indigo-700" /> Transportauftrag erstellen
                    </DialogTitle>
                    <DialogDescription>{vehLabel(item)}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Von</label>
                            <Input value={f.from_location} onChange={(e) => set("from_location", e.target.value)} placeholder="Abhol-Standort" className="rounded-sm mt-1" data-testid="transport-from" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nach</label>
                            <Input value={f.to_location} onChange={(e) => set("to_location", e.target.value)} placeholder="Ziel / Kundenadresse" className="rounded-sm mt-1" data-testid="transport-to" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Geplantes Datum</label>
                            <Input type="date" value={f.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className="rounded-sm mt-1" data-testid="transport-date" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zeitfenster</label>
                            <Input value={f.time_window} onChange={(e) => set("time_window", e.target.value)} placeholder="z. B. 08–12 Uhr" className="rounded-sm mt-1" data-testid="transport-window" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Transporteur</label>
                            <Input value={f.carrier} onChange={(e) => set("carrier", e.target.value)} placeholder="Spedition / Eigen" className="rounded-sm mt-1" data-testid="transport-carrier" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrer</label>
                            <Input value={f.driver} onChange={(e) => set("driver", e.target.value)} placeholder="Name" className="rounded-sm mt-1" data-testid="transport-driver" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Notiz</label>
                        <Textarea value={f.note} onChange={(e) => set("note", e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="transport-note" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                    <Button variant="outline" onClick={() => submit(true)} disabled={busy} className="rounded-sm" data-testid="transport-submit-goto">
                        Anlegen & zur Planung
                    </Button>
                    <Button onClick={() => submit(false)} disabled={busy} className="rounded-sm bg-indigo-700 hover:bg-indigo-800 text-white font-bold" data-testid="transport-submit">
                        {busy ? "Lege an…" : "Anlegen"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
